<template>
  <div v-if="quiz" class="quiz-player-container d-flex align-center justify-center pa-4">
    <div class="w-100" style="max-width: 800px;">
      <!-- Result Screen -->
      <v-card v-if="result" class="result-card text-center pa-8 rounded-xl" :class="{ 'is-passed': result.passed }" dark elevation="24">
        <!-- Confetti -->
        <div v-if="result.passed" class="confetti-wrap" aria-hidden="true">
          <span v-for="n in 28" :key="n" class="confetti" :style="confettiStyle(n)"></span>
        </div>

        <div class="result-icon-wrap" :class="{ pulse: result.passed }">
          <v-icon :color="result.passed ? '#D4AF37' : 'error'" size="86">
            {{ result.passed ? "mdi-trophy" : "mdi-close-circle" }}
          </v-icon>
        </div>

        <h2 class="text-h4 font-weight-bold mb-1 mt-3" :class="{ 'gold-text-grad': result.passed }">
          {{ result.passed ? "Bestanden!" : "Leider nicht bestanden" }}
        </h2>
        <p class="text-subtitle-1 grey--text mb-2">{{ result.passed ? "Stark! Du hast das Modul abgeschlossen." : "Versuch's nochmal." }}</p>
        <div class="score-circle mb-6 mx-auto">
          <span class="score-value">{{ result.score }}<small>%</small></span>
        </div>

        <v-btn v-if="!result.passed" color="#D4AF37" class="black--text font-weight-bold" rounded large @click="restart">
          <v-icon left>mdi-restart</v-icon> Nochmal versuchen
        </v-btn>
        <v-btn v-else color="#D4AF37" class="black--text font-weight-bold" rounded large @click="$router.push('/akademie/suite')">
          <v-icon left>mdi-arrow-left</v-icon> Zurück zur Übersicht
        </v-btn>
      </v-card>

      <!-- Question Screen -->
      <v-card v-else class="question-card pa-8 rounded-xl" dark elevation="0">
        <div class="d-flex justify-space-between align-center mb-6">
          <span class="gold-text font-weight-bold">Frage {{ currentIndex + 1 }} von {{ quiz.questions.length }}</span>
          <v-btn icon color="white" @click="$router.back()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-progress-linear
          :value="progressPercent"
          color="#D4AF37"
          height="8"
          rounded
          background-color="rgba(255,255,255,0.1)"
          class="mb-8"
        ></v-progress-linear>

        <h2 class="text-h5 font-weight-bold mb-8 lh-15">{{ currentQuestion.question }}</h2>

        <div class="options-list">
          <v-card
            v-for="(opt, idx) in currentQuestion.options"
            :key="idx"
            class="option-card mb-4 pa-4 rounded-lg d-flex align-center"
            :class="{ selected: isSelected(idx) }"
            @click="toggleOption(idx)">
            <v-checkbox :input-value="isSelected(idx)" color="#D4AF37" hide-details class="mt-0 pt-0 mr-3 pointer-events-none"></v-checkbox>
            <span class="text-body-1">{{ opt.text }}</span>
          </v-card>
        </div>

        <div class="d-flex justify-end mt-8">
          <v-btn
            color="#D4AF37"
            class="black--text font-weight-bold px-8"
            rounded
            large
            :disabled="!hasSelection"
            @click="nextOrSubmit"
            :loading="submitting">
            {{ isLastQuestion ? "Abschliessen" : "Weiter" }} <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import api from "@/api";

export default {
  name: "QuizPlayer",
  data() {
    return {
      quiz: null,
      currentIndex: 0,
      answers: [],
      result: null,
      submitting: false,
      loading: false,
    }
  },
  computed: {
    currentQuestion() {
      if (!this.quiz || !this.quiz.questions) return null
      return this.quiz.questions[this.currentIndex]
    },
    isLastQuestion() {
      if (!this.quiz || !this.quiz.questions) return false
      return this.currentIndex === this.quiz.questions.length - 1
    },
    progressPercent() {
      if (!this.quiz || !this.quiz.questions) return 0
      return ((this.currentIndex + 1) / this.quiz.questions.length) * 100
    },
    currentAnswerObj() {
      let ans = this.answers.find((a) => a.questionIndex === this.currentIndex)
      if (!ans) {
        ans = { questionIndex: this.currentIndex, selectedOptions: [] }
        this.answers.push(ans)
      }
      return ans
    },
    hasSelection() {
      return this.currentAnswerObj.selectedOptions.length > 0
    },
  },
  mounted() {
    this.loadQuiz()
  },
  methods: {
    async loadQuiz() {
      this.loading = true
      try {
        const mod = await api.akademie.fetchModule(this.$route.params.id)
        if (mod && mod.quiz) {
          this.quiz = mod.quiz
        }
      } catch (err) {
        console.error("Fehler beim Laden des Quiz:", err)
      } finally {
        this.loading = false
      }
    },
    isSelected(idx) {
      return this.currentAnswerObj.selectedOptions.includes(idx)
    },
    toggleOption(idx) {
      const selected = this.currentAnswerObj.selectedOptions
      const pos = selected.indexOf(idx)
      if (pos > -1) {
        selected.splice(pos, 1)
      } else {
        selected.push(idx)
      }
    },
    async nextOrSubmit() {
      if (this.isLastQuestion) {
        this.submit()
      } else {
        this.currentIndex++
      }
    },
    async submit() {
      this.submitting = true
      try {
        const res = await api.akademie.submitQuiz(this.$route.params.id, this.answers)
        this.result = res // { passed: true, score: 100 }
      } catch (error) {
        console.error("Fehler beim Senden des Quiz:", error)
      } finally {
        this.submitting = false
      }
    },
    restart() {
      this.currentIndex = 0
      this.answers = []
      this.result = null
    },
    confettiStyle(n) {
      const colors = ["#D4AF37", "#FFD700", "#fff7d6", "#ffce54", "#ffeaa0"]
      const left = (n * 37) % 100
      const delay = ((n * 113) % 1000) / 1000
      const dur = 2.4 + ((n * 31) % 16) / 10
      const rot = (n * 47) % 360
      const size = 6 + (n % 6)
      return {
        left: left + "%",
        background: colors[n % colors.length],
        animationDelay: delay + "s",
        animationDuration: dur + "s",
        transform: "rotate(" + rot + "deg)",
        width: size + "px",
        height: size * 1.6 + "px",
      }
    },
  },
}
</script>

<style scoped>
.quiz-player-container {
  min-height: 100vh;
  background-color: #121212;
}
.w-100 {
  width: 100%;
}

.question-card,
.result-card {
  background: rgba(30, 30, 30, 0.9) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.gold-text {
  color: #d4af37;
}
.lh-15 {
  line-height: 1.5;
}

.option-card {
  background: rgba(18, 18, 18, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-card:hover {
  background: rgba(40, 40, 40, 0.8) !important;
}

.option-card.selected {
  border-color: #d4af37;
  background: rgba(212, 175, 55, 0.1) !important;
}

.pointer-events-none {
  pointer-events: none;
}

/* === RESULT ANIMATION === */
.result-card {
  position: relative;
  overflow: hidden;
}
.result-card.is-passed {
  background: linear-gradient(135deg, rgba(35, 30, 20, 0.95), rgba(20, 20, 20, 0.95)) !important;
  border: 1px solid rgba(212, 175, 55, 0.4);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(212, 175, 55, 0.2);
}

.result-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(212, 175, 55, 0.18), transparent 70%);
  margin-bottom: 8px;
}
.result-icon-wrap.pulse {
  animation: trophyIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both, pulseGlow 2.4s ease-in-out 0.6s infinite;
}

@keyframes trophyIn {
  0% { transform: scale(0.2) rotate(-15deg); opacity: 0; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}
@keyframes pulseGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4); }
  50% { box-shadow: 0 0 0 18px rgba(212, 175, 55, 0); }
}

.gold-text-grad {
  background: linear-gradient(45deg, #d4af37, #ffd700, #fff7d6, #d4af37);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: goldShimmer 3s linear infinite;
}
@keyframes goldShimmer {
  0% { background-position: 0% center; }
  100% { background-position: 200% center; }
}

.score-circle {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 2px solid rgba(212, 175, 55, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  animation: scoreIn 0.6s ease 0.3s both;
}
@keyframes scoreIn {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.score-value {
  font-size: 2rem;
  font-weight: 700;
  color: #d4af37;
  font-variant-numeric: tabular-nums;
}
.score-value small {
  font-size: 0.9rem;
  opacity: 0.7;
}

/* Confetti */
.confetti-wrap {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}
.confetti {
  position: absolute;
  top: -20px;
  border-radius: 2px;
  animation-name: confettiFall;
  animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
  opacity: 0.9;
}
@keyframes confettiFall {
  0% { top: -20px; opacity: 0; transform: rotate(0deg); }
  10% { opacity: 1; }
  100% { top: 110%; transform: rotate(720deg); opacity: 0.8; }
}
</style>
